import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Save, Plus, AlertTriangle, Trash2, MapPin, CalendarIcon } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { SitesManager } from '../components/SitesManager';
import { CustomerPricesManager } from '../components/CustomerPricesManager';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { useConfirm } from '../context/ConfirmContext';

export const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isNew = id === 'new';
  const sitesManagerRef = useRef();
  const pricesManagerRef = useRef();
  const [activeTab, setActiveTab] = useState('data');
  const [hasChanges, setHasChanges] = useState(false);

  const [customer, setCustomer] = useState({
    name_or_company: '',
    has_problem: false,
    problem_notes: '',
    phone_mobile: '',
    phone_office: '',
    contact_email: '',
    address_street: '',
    address_zip: '',
    address_city: '',
    address_canton: '',
    address_country: 'CH',
    use_address_for_billing: true,
    billing_address_street: '',
    billing_address_zip: '',
    billing_address_city: '',
    billing_address_canton: '',
    billing_address_country: 'CH',
    billing_email: '',
    billing_email_cc: '',
    uid_che: '',
    document_language: 'IT',
    payment_terms_days: 30,
    invoice_delivery_method: 'EMAIL',
    notes: ''
  });

  const [contacts, setContacts] = useState([]);
  const [activeSites, setActiveSites] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Contact form
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    first_name: '', last_name: '', phone: '', email: '', date_of_birth: '', notes: ''
  });

  const updateCustomer = (updates) => {
    setCustomer(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  useEffect(() => {
    if (!isNew) {
      loadCustomer();
      loadContacts();
      loadActiveSites();
    }
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (editingContact) {
      setContactForm({
        first_name: editingContact.first_name || '',
        last_name: editingContact.last_name || '',
        phone: editingContact.phone || '',
        email: editingContact.email || '',
        date_of_birth: editingContact.date_of_birth || '',
        notes: editingContact.notes || ''
      });
    } else {
      setContactForm({ first_name: '', last_name: '', phone: '', email: '', date_of_birth: '', notes: '' });
    }
  }, [editingContact]);

  const loadCustomer = async () => {
    try {
      const response = await api.getCustomer(id);
      const data = response.data;
      const customerData = {
        ...data,
        address_street: data.address_street || data.billing_address_street || '',
        address_zip: data.address_zip || data.billing_address_zip || '',
        address_city: data.address_city || data.billing_address_city || '',
        address_canton: data.address_canton || data.billing_address_canton || '',
        address_country: data.address_country || data.billing_address_country || 'CH',
        contact_email: data.contact_email || '',
        use_address_for_billing: data.use_address_for_billing !== false,
        has_problem: data.has_problem || false,
        problem_notes: data.problem_notes || ''
      };
      setCustomer(customerData);
      setHasChanges(false);
    } catch (error) {
      toast.error('Errore nel caricamento cliente');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await api.getCustomerContacts(id);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const loadActiveSites = async () => {
    try {
      const response = await api.getCustomerSites(id);
      setActiveSites(response.data.filter(s => s.status === 'ACTIVE'));
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const handleSaveContact = async () => {
    if (!contactForm.first_name || !contactForm.last_name) {
      toast.error('Nome e cognome sono obbligatori');
      return;
    }
    try {
      if (editingContact) {
        await api.updateCustomerContact(id, editingContact.id, contactForm);
        toast.success('Contatto aggiornato');
      } else {
        await api.createCustomerContact(id, contactForm);
        toast.success('Contatto aggiunto');
      }
      setShowContactForm(false);
      setEditingContact(null);
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio contatto');
    }
  };

  const handleDeleteContact = async (contactId) => {
    const ok = await confirm({
      title: 'Elimina contatto',
      description: 'Il contatto sarà rimosso dal cliente.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteCustomerContact(id, contactId);
      toast.success('Contatto eliminato');
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!customer.name_or_company || !customer.name_or_company.trim()) {
      toast.error('Il nome / ragione sociale è obbligatorio');
      return;
    }
    setSaving(true);
    try {
      const dataToSend = { ...customer };
      if (customer.use_address_for_billing) {
        dataToSend.billing_address_street = customer.address_street;
        dataToSend.billing_address_zip = customer.address_zip;
        dataToSend.billing_address_city = customer.address_city;
        dataToSend.billing_address_canton = customer.address_canton;
        dataToSend.billing_address_country = customer.address_country;
      }
      if (!dataToSend.billing_email && dataToSend.contact_email) {
        dataToSend.billing_email = dataToSend.contact_email;
      }

      // Convert empty strings to null for Optional fields (avoids Pydantic EmailStr 422)
      const nullableFields = [
        'contact_email', 'billing_email', 'billing_email_cc',
        'phone_mobile', 'phone_office', 'address_street', 'address_zip',
        'address_city', 'address_canton', 'billing_address_street',
        'billing_address_zip', 'billing_address_city', 'billing_address_canton',
        'uid_che', 'notes', 'problem_notes'
      ];
      nullableFields.forEach(f => {
        if (dataToSend[f] === '') dataToSend[f] = null;
      });

      if (isNew) {
        const response = await api.createCustomer(dataToSend);
        toast.success('Cliente creato con successo');
        navigate(`/app/customers/${response.data.id}`);
      } else {
        await api.updateCustomer(id, dataToSend);
        toast.success('Cliente aggiornato con successo');
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Customer save error:', error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      let message = 'Errore nel salvataggio';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic 422 validation errors → format as readable string
        message = detail
          .map(err => {
            const field = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : '';
            return field ? `${field}: ${err.msg}` : err.msg;
          })
          .join(' | ');
      }
      toast.error(message, { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8">Caricamento...</div>;

  return (
    <div data-testid="customer-detail-page">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/app/customers')} className="mb-4" data-testid="back-button">
          <ArrowLeft size={18} className="mr-2" /> Torna ai Clienti
        </Button>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Nuovo Cliente' : customer.name_or_company}
          </h1>
          {customer.has_problem && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
              <AlertTriangle className="h-3 w-3" /> Problema
            </span>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setShowContactForm(false); setEditingContact(null); }} className="space-y-6">
        <div className="flex justify-between items-center sticky top-0 z-10 bg-background py-2 -mx-1 px-1">
          <TabsList>
            <TabsTrigger value="data">Dati Cliente</TabsTrigger>
            {!isNew && <TabsTrigger value="contacts">Contatti</TabsTrigger>}
            {!isNew && <TabsTrigger value="sites">Progetti</TabsTrigger>}
            {!isNew && <TabsTrigger value="pricing">Prezzi Cliente</TabsTrigger>}
          </TabsList>
          <div className="flex items-center gap-2">
            {activeTab === 'data' && hasChanges && (
              <Button onClick={handleSubmit} disabled={saving} size="sm" data-testid="save-header-btn">
                <Save size={16} className="mr-1" />
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            )}
            {activeTab === 'contacts' && !isNew && (
              <Button onClick={() => { setEditingContact(null); setShowContactForm(true); }} data-testid="new-contact-top-btn">
                <Plus size={18} className="mr-2" /> Nuovo Contatto
              </Button>
            )}
            {activeTab === 'sites' && !isNew && (
              <Button onClick={() => sitesManagerRef.current?.addSite()} data-testid="new-site-btn">
                <Plus size={18} className="mr-2" /> Nuovo Progetto
              </Button>
            )}
            {activeTab === 'pricing' && !isNew && (
              <Button onClick={() => pricesManagerRef.current?.addPrice()} data-testid="new-price-top-btn">
                <Plus size={18} className="mr-2" /> Nuovo Prezzo
              </Button>
            )}
          </div>
        </div>

        {/* ===== TAB DATI ===== */}
        <TabsContent value="data">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Informazioni Cliente</CardTitle>
                {!isNew && customer.customer_number && (
                  <span className="text-sm font-mono bg-slate-100 px-3 py-1 rounded-full text-slate-600" data-testid="customer-number">
                    Nr. Cliente: {String(customer.customer_number).padStart(6, '0')}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* Flag Problema */}
                <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <Checkbox
                    id="has_problem"
                    checked={customer.has_problem}
                    onCheckedChange={(checked) => updateCustomer({ has_problem: checked })}
                    data-testid="has-problem-checkbox"
                  />
                  <div className="flex-1">
                    <label htmlFor="has_problem" className="text-sm font-medium cursor-pointer">Segnala problema con questo cliente</label>
                    <p className="text-xs text-slate-500">Il cliente viene evidenziato nella lista (es. mancati pagamenti)</p>
                    {customer.has_problem && (
                      <Textarea
                        value={customer.problem_notes || ''}
                        onChange={(e) => updateCustomer({ problem_notes: e.target.value })}
                        placeholder="Descrivi il problema..."
                        rows={2}
                        className="mt-2"
                        data-testid="problem-notes"
                      />
                    )}
                  </div>
                </div>

                {/* Nome / Ditta */}
                <div className="space-y-2">
                  <Label htmlFor="name_or_company">Nome Cognome / Ditta *</Label>
                  <Input
                    id="name_or_company"
                    value={customer.name_or_company}
                    onChange={(e) => updateCustomer({ name_or_company: e.target.value })}
                    placeholder="Es: Mario Rossi oppure Rossi SA"
                    required
                    data-testid="customer-name-input"
                  />
                </div>

                {/* Sezione Contatti */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-base mb-4">Contatti</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone_mobile">Telefono Cellulare</Label>
                      <Input id="phone_mobile" value={customer.phone_mobile || ''} onChange={(e) => updateCustomer({ phone_mobile: e.target.value })} placeholder="+41 79 123 45 67" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone_office">Telefono Ufficio</Label>
                      <Input id="phone_office" value={customer.phone_office || ''} onChange={(e) => updateCustomer({ phone_office: e.target.value })} placeholder="+41 91 123 45 67" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Email di Contatto</Label>
                      <Input id="contact_email" type="email" value={customer.contact_email || ''} onChange={(e) => updateCustomer({ contact_email: e.target.value })} placeholder="info@esempio.ch" />
                    </div>
                  </div>
                </div>

                {/* Sezione Indirizzo */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-base mb-4">Indirizzo</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="address_street">Via *</Label>
                      <Input id="address_street" value={customer.address_street || ''} onChange={(e) => updateCustomer({ address_street: e.target.value })} required />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="address_zip">NAP *</Label>
                        <Input id="address_zip" value={customer.address_zip || ''} onChange={(e) => updateCustomer({ address_zip: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_city">Localita *</Label>
                        <Input id="address_city" value={customer.address_city || ''} onChange={(e) => updateCustomer({ address_city: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_canton">Cantone *</Label>
                        <Input id="address_canton" value={customer.address_canton || ''} onChange={(e) => updateCustomer({ address_canton: e.target.value })} required />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Checkbox fatturazione */}
                <div className="flex items-center gap-2 py-2">
                  <Checkbox
                    id="use_address_for_billing"
                    checked={customer.use_address_for_billing}
                    onCheckedChange={(checked) => updateCustomer({ use_address_for_billing: checked })}
                    data-testid="use-address-for-billing"
                  />
                  <label htmlFor="use_address_for_billing" className="text-sm font-medium cursor-pointer">
                    Usa questo indirizzo anche per la fatturazione
                  </label>
                </div>

                {/* Sezione Fatturazione */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-base mb-4">Fatturazione</h3>

                  {/* Indirizzo fatturazione separato */}
                  {!customer.use_address_for_billing && (
                    <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-sm text-amber-800 mb-3">Indirizzo di Fatturazione</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Via *</Label>
                          <Input value={customer.billing_address_street || ''} onChange={(e) => updateCustomer({ billing_address_street: e.target.value })} required />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>NAP *</Label>
                            <Input value={customer.billing_address_zip || ''} onChange={(e) => updateCustomer({ billing_address_zip: e.target.value })} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Localita *</Label>
                            <Input value={customer.billing_address_city || ''} onChange={(e) => updateCustomer({ billing_address_city: e.target.value })} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Cantone *</Label>
                            <Input value={customer.billing_address_canton || ''} onChange={(e) => updateCustomer({ billing_address_canton: e.target.value })} required />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="billing_email">Email Fatturazione *</Label>
                      <Input id="billing_email" type="email" value={customer.billing_email || ''} onChange={(e) => updateCustomer({ billing_email: e.target.value })}
                        placeholder={customer.contact_email || 'fatture@esempio.ch'} required data-testid="billing-email-input" />
                      {customer.contact_email && !customer.billing_email && (
                        <p className="text-xs text-slate-400">Lascia vuoto per usare l'email di contatto</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billing_email_cc">Email CC</Label>
                      <Input id="billing_email_cc" type="email" value={customer.billing_email_cc || ''} onChange={(e) => updateCustomer({ billing_email_cc: e.target.value })} placeholder="copia@esempio.ch" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="uid_che">UID/CHE</Label>
                      <Input id="uid_che" value={customer.uid_che || ''} onChange={(e) => updateCustomer({ uid_che: e.target.value })} placeholder="CHE-123.456.789" />
                    </div>
                    <div className="space-y-2">
                      <Label>Lingua Documenti</Label>
                      <Select value={customer.document_language} onValueChange={(value) => updateCustomer({ document_language: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="IT">Italiano</SelectItem>
                          <SelectItem value="DE">Tedesco</SelectItem>
                          <SelectItem value="FR">Francese</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Condizioni di Pagamento</Label>
                      <div className="flex items-center gap-2">
                        <Input type="number" min="0" className="w-24" value={customer.payment_terms_days || 30}
                          onChange={(e) => updateCustomer({ payment_terms_days: parseInt(e.target.value) || 30 })} />
                        <span className="text-sm text-slate-500">giorni</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Metodo Invio Fattura</Label>
                      <Select value={customer.invoice_delivery_method} onValueChange={(value) => updateCustomer({ invoice_delivery_method: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EMAIL">Email</SelectItem>
                          <SelectItem value="PAPER">Cartacea</SelectItem>
                          <SelectItem value="BOTH">Email e Cartacea</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2 mt-4">
                    <Label htmlFor="notes">Note</Label>
                    <Textarea id="notes" value={customer.notes || ''} onChange={(e) => updateCustomer({ notes: e.target.value })} rows={3} />
                  </div>
                </div>

              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB CONTATTI ===== */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <CardTitle>Contatti</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Contact Form */}
              {showContactForm && (
                <div className="mb-6 p-4 border rounded-lg bg-slate-50 space-y-3" data-testid="contact-form">
                  <h4 className="font-medium text-sm">{editingContact ? 'Modifica Contatto' : 'Nuovo Contatto'}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome *</Label>
                      <Input value={contactForm.first_name} onChange={(e) => setContactForm({...contactForm, first_name: e.target.value})} data-testid="contact-first-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Cognome *</Label>
                      <Input value={contactForm.last_name} onChange={(e) => setContactForm({...contactForm, last_name: e.target.value})} data-testid="contact-last-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Telefono</Label>
                      <Input value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Data di Nascita</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className={`w-full justify-start text-left font-normal h-9 ${!contactForm.date_of_birth ? 'text-muted-foreground' : ''}`} data-testid="contact-dob">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {contactForm.date_of_birth ? new Date(contactForm.date_of_birth + 'T00:00:00').toLocaleDateString('it-CH') : 'Seleziona data...'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={contactForm.date_of_birth ? new Date(contactForm.date_of_birth + 'T00:00:00') : undefined}
                            onSelect={(date) => {
                              if (date) {
                                const y = date.getFullYear();
                                const m = String(date.getMonth() + 1).padStart(2, '0');
                                const d = String(date.getDate()).padStart(2, '0');
                                setContactForm({...contactForm, date_of_birth: `${y}-${m}-${d}`});
                              } else {
                                setContactForm({...contactForm, date_of_birth: ''});
                              }
                            }}
                            captionLayout="dropdown-buttons"
                            fromYear={1930}
                            toYear={new Date().getFullYear()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Note</Label>
                    <Input value={contactForm.notes} onChange={(e) => setContactForm({...contactForm, notes: e.target.value})} />
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveContact} data-testid="save-contact-btn">Salva</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setShowContactForm(false); setEditingContact(null); }}>Annulla</Button>
                    </div>
                    {editingContact && (
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { handleDeleteContact(editingContact.id); setShowContactForm(false); setEditingContact(null); }} data-testid="delete-contact-btn">
                        <Trash2 className="h-4 w-4 mr-1" /> Elimina contatto
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Contacts List */}
              {contacts.length === 0 && !showContactForm ? (
                <p className="text-slate-500 text-sm text-center py-6">Nessun contatto aggiunto</p>
              ) : (
                <div className="space-y-2">
                  {contacts.map(contact => {
                    // Find active sites where this contact is manager
                    const managedSites = activeSites.filter(s =>
                      s.manager_contact_id === contact.id ||
                      (contact.managed_site_ids || []).includes(s.id) ||
                      (s.manager_first_name?.toLowerCase() === contact.first_name?.toLowerCase() &&
                       s.manager_last_name?.toLowerCase() === contact.last_name?.toLowerCase() &&
                       s.manager_first_name && s.manager_last_name)
                    );
                    return (
                      <div
                        key={contact.id}
                        className="p-3 border rounded-lg hover:bg-secondary cursor-pointer transition-colors"
                        onClick={() => { setEditingContact(contact); setShowContactForm(true); }}
                        data-testid={`contact-${contact.id}`}
                      >
                        <p className="font-medium text-sm">{contact.first_name} {contact.last_name}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                          {contact.email && <span>{contact.email}</span>}
                          {contact.phone && <span>{contact.phone}</span>}
                          {contact.date_of_birth && <span>Nato: {new Date(contact.date_of_birth).toLocaleDateString('it-CH')}</span>}
                        </div>
                        {contact.notes && <p className="text-xs text-slate-400 mt-0.5">{contact.notes}</p>}
                        {managedSites.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {managedSites.map(site => (
                              <span key={site.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100">
                                <MapPin className="h-3 w-3" /> {site.site_name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB PROGETTI ===== */}
        <TabsContent value="sites">
          <Card>
            <CardHeader><CardTitle>Progetti</CardTitle></CardHeader>
            <CardContent>
              <SitesManager ref={sitesManagerRef} customerId={id} customerBillingEmail={customer.billing_email} contacts={contacts} onContactsChanged={loadContacts} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB PREZZI ===== */}
        <TabsContent value="pricing">
          <Card>
            <CardHeader><CardTitle>Prezzi Specifici Cliente</CardTitle></CardHeader>
            <CardContent>
              <CustomerPricesManager ref={pricesManagerRef} customerId={id} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
